import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Card, Button, Badge, Row, Col, Alert } from 'react-bootstrap';
import {
  FaArrowLeft,
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
} from 'react-icons/fa';
import dayjs from 'dayjs';
import { useIncidentActions } from './hooks/incidentActionHooks';
import ActionDrawer from './ActionDrawer';
import { INCIDENT } from './ICSConstants';
import Loading from '../Loading/Loading';
import PageTitle from '../headers/PageTitle';

const ActionView: React.FC = () => {
  const { incidentId, actionId } = useParams<{
    incidentId: string;
    actionId: string;
  }>();
  const navigate = useNavigate();
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const { data: actions, isLoading } = useIncidentActions();

  // Find the specific action
  const action = actions?.find((a) => a.action_id === actionId);

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return 'Not set';
    return dayjs(date).format('MM/DD/YYYY HH:mm');
  };

  const getStatusBadgeClass = (status: string | null) => {
    switch (status) {
      case 'Planned':
        return 'bg-info';
      case 'Current/Ongoing':
        return 'bg-warning';
      case 'Closed':
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  };

  const isOverdue = () => {
    if (!action?.target_date) return false;
    const now = new Date();
    const target = new Date(action.target_date);
    return (
      (action.status === 'Current/Ongoing' || action.status === 'Closed') &&
      target < now
    );
  };

  // Navigate back to the previous view (table or kanban)
  const handleBack = () => {
    // Check if we came from a specific view
    const referrer = document.referrer;
    const currentPath = `/iap/${incidentId}/actions`;

    if (referrer.includes(currentPath)) {
      // Go back to the previous page
      navigate(-1);
    } else {
      // Default to actions page (will use stored view preference)
      navigate(`/iap/${incidentId}/actions`);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!action) {
    return (
      <div className="maincontent">
        <Alert variant="warning">
          <h4>Action Not Found</h4>
          <p>The requested action could not be found.</p>
          <Button variant="primary" onClick={handleBack}>
            <FaArrowLeft className="me-2" />
            Back to Actions
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="maincontent">
      <PageTitle title={action.name} onBack={handleBack}>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleBack}>
            <FaArrowLeft className="me-2" />
            Back to Actions
          </Button>
          <Button variant="primary" onClick={() => setShowEditDrawer(true)}>
            <FaEdit className="me-2" />
            Edit Action
          </Button>
        </div>
      </PageTitle>

      <Card
        style={{
          backgroundColor: 'var(--background-dark)',
          border: '1px solid var(--border-neutral)',
        }}
      >
        <Card.Header
          style={{
            backgroundColor: 'var(--background-light)',
            borderBottom: '1px solid var(--border-neutral)',
          }}
        >
          <h5 className="mb-0 text-white">Action Details</h5>
        </Card.Header>
        <Card.Body style={{ color: 'var(--text-white)' }}>
          <Row className="mb-3">
            <Col sm={3}>
              <strong>Status:</strong>
            </Col>
            <Col sm={9}>
              <Badge className={getStatusBadgeClass(action.status)}>
                {action.status}
              </Badge>
              {isOverdue() && (
                <Badge bg="danger" className="ms-2">
                  Overdue
                </Badge>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3}>
              <strong>Description:</strong>
            </Col>
            <Col sm={9}>{action.description || 'No description provided'}</Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3}>
              <strong>POC (Point of Contact):</strong>
            </Col>
            <Col sm={9}>
              <span>{action.poc_user_name || 'Unassigned'}</span>
              {action.briefed_poc && (
                <Badge bg="success" className="ms-2">
                  <FaCheckCircle className="me-1" />
                  Briefed
                </Badge>
              )}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3}>
              <strong>Start Date:</strong>
            </Col>
            <Col sm={9}>{formatDate(action.start_date)}</Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3}>
              <strong>Target Date:</strong>
            </Col>
            <Col sm={9}>{formatDate(action.target_date)}</Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3}>
              <strong>Actual Date:</strong>
            </Col>
            <Col sm={9}>{formatDate(action.actual_date)}</Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3}>
              <strong>Created By:</strong>
            </Col>
            <Col sm={9}>{action.user_name || 'Unknown'}</Col>
          </Row>

          <Row className="mb-3">
            <Col sm={3}>
              <strong>Last Updated:</strong>
            </Col>
            <Col sm={9}>{formatDate(action.timestamp)}</Col>
          </Row>

          {action.checklist && action.checklist.length > 0 && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3">Checklist</h6>
              <ul className="list-unstyled">
                {action.checklist.map((item: any, index: number) => {
                  const isCompleted = item.completed || item.checked;
                  const itemText =
                    typeof item === 'string'
                      ? item
                      : item.text || item.label || '';

                  return (
                    <li key={index} className="mb-2">
                      <div className="d-flex align-items-start">
                        {isCompleted ? (
                          <FaCheckCircle className="text-success me-2 mt-1" />
                        ) : (
                          <FaTimesCircle className="text-muted me-2 mt-1" />
                        )}
                        <span
                          className={
                            isCompleted ? 'text-decoration-line-through' : ''
                          }
                        >
                          {itemText}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {action.resource_details && action.resource_details.length > 0 && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3">Assigned Resources</h6>
              <ul className="list-unstyled">
                {action.resource_details.map((resource: any, index: number) => (
                  <li key={index} className="mb-2">
                    <strong>{resource.name}</strong>
                    <div className="text-muted small">
                      {resource.kind} - {resource.type}
                      {resource.quantity && ` (Qty: ${resource.quantity})`}
                    </div>
                    {resource.status && (
                      <Badge bg="secondary" className="mt-1">
                        {resource.status}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card.Body>
      </Card>

      {showEditDrawer && (
        <ActionDrawer
          scope={INCIDENT}
          toggle={() => setShowEditDrawer(false)}
          selectedEntity={action}
        />
      )}
    </div>
  );
};

export default ActionView;
